import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders, handleApiError } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Search, Phone, MapPin, Edit, Trash2, MessageCircle, Store, Building2, ShoppingBag, Settings, X, Printer, CheckCircle, Archive, RotateCcw, ChevronDown, Check, Route, Edit2, Combine, Save, User } from 'lucide-react';
import { toast } from 'sonner';
import { useCallCustomer } from '../../hooks/useCallCustomer';

// Enhanced Route Multi-Select Dropdown with Edit and Combine features
const RouteDropdown = ({ options, selected, onChange, placeholder, label, onRouteRename, onRoutesCombine }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [pendingEditRoute, setPendingEditRoute] = useState(null);
  const [showCombineModal, setShowCombineModal] = useState(false);
  const [combineSelected, setCombineSelected] = useState([]);
  const [newCombinedName, setNewCombinedName] = useState('');
  const dropdownRef = useRef(null);
  const editInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (!showEditConfirm && !showCombineModal) {
          setIsOpen(false);
          setEditingRoute(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEditConfirm, showCombineModal]);

  useEffect(() => {
    if (editingRoute && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingRoute]);

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const selectAll = () => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const getDisplayText = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length === options.length) return `All ${label}`;
    if (selected.length === 1) return selected[0];
    return `${selected.length} ${label}`;
  };

  const handleEditClick = (e, route) => {
    e.stopPropagation();
    setPendingEditRoute(route);
    setShowEditConfirm(true);
  };

  const confirmEdit = () => {
    setEditingRoute(pendingEditRoute);
    setEditValue(pendingEditRoute);
    setShowEditConfirm(false);
    setPendingEditRoute(null);
  };

  const cancelEdit = () => {
    setShowEditConfirm(false);
    setPendingEditRoute(null);
  };

  const saveEdit = async (e) => {
    e.stopPropagation();
    if (editValue.trim() && editValue !== editingRoute) {
      await onRouteRename(editingRoute, editValue.trim());
    }
    setEditingRoute(null);
    setEditValue('');
  };

  const cancelEditMode = (e) => {
    e.stopPropagation();
    setEditingRoute(null);
    setEditValue('');
  };

  const handleCombineClick = () => {
    setCombineSelected([]);
    setNewCombinedName('');
    setShowCombineModal(true);
  };

  const toggleCombineSelection = (route) => {
    if (combineSelected.includes(route)) {
      setCombineSelected(combineSelected.filter(r => r !== route));
    } else {
      setCombineSelected([...combineSelected, route]);
    }
  };

  const executeCombine = async () => {
    if (combineSelected.length < 2) {
      toast.error('Please select at least 2 routes to combine');
      return;
    }
    if (!newCombinedName.trim()) {
      toast.error('Please enter a name for the combined route');
      return;
    }
    await onRoutesCombine(combineSelected, newCombinedName.trim());
    setShowCombineModal(false);
    setCombineSelected([]);
    setNewCombinedName('');
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between h-8 min-w-[140px] max-w-[200px] px-2 text-sm border border-slate-200 rounded-md bg-white hover:bg-slate-50"
        >
          <span className="truncate text-left">{getDisplayText()}</span>
          <ChevronDown className={`h-4 w-4 ml-1 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute z-50 mt-1 min-w-[280px] max-w-[350px] bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-auto">
            {/* Header with Combine Button */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50 sticky top-0">
              <span className="text-xs font-semibold text-slate-600 uppercase">Routes</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 px-2 text-xs border-blue-200 text-blue-600 hover:bg-blue-50"
                onClick={handleCombineClick}
              >
                <Combine className="h-3 w-3 mr-1" />
                Combine
              </Button>
            </div>
            
            {/* Select All Option */}
            <div
              className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 cursor-pointer border-b border-slate-100"
              onClick={selectAll}
            >
              <div className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${selected.length === options.length ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                {selected.length === options.length && <Check className="h-3 w-3 text-white" />}
              </div>
              <span className="text-sm font-medium">Select All</span>
            </div>
            
            {/* Individual Options */}
            {options.map((option) => (
              <div
                key={option}
                className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 group"
              >
                {editingRoute === option ? (
                  // Edit mode
                  <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                    <Input
                      ref={editInputRef}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="h-7 text-sm flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(e);
                        if (e.key === 'Escape') cancelEditMode(e);
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white"
                      onClick={saveEdit}
                    >
                      <Save className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2"
                      onClick={cancelEditMode}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  // Normal mode
                  <>
                    <div 
                      className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 cursor-pointer ${selected.includes(option) ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}
                      onClick={() => toggleOption(option)}
                    >
                      {selected.includes(option) && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span 
                      className="text-sm flex-1 cursor-pointer whitespace-normal break-words leading-tight py-0.5"
                      onClick={() => toggleOption(option)}
                    >
                      {option}
                    </span>
                    <button
                      type="button"
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-blue-100 rounded transition-opacity"
                      onClick={(e) => handleEditClick(e, option)}
                      title="Edit route name"
                    >
                      <Edit2 className="h-3 w-3 text-blue-600" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Confirmation Dialog */}
      <Dialog open={showEditConfirm} onOpenChange={setShowEditConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-blue-600" />
              Edit Route?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to edit this route? This will rename "<strong>{pendingEditRoute}</strong>" for all stores assigned to it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={cancelEdit}>
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={confirmEdit}>
              Yes, Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Combine Routes Modal */}
      <Dialog open={showCombineModal} onOpenChange={setShowCombineModal}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Combine className="h-5 w-5 text-blue-600" />
              Combine Routes
            </DialogTitle>
            <DialogDescription>
              Select two or more routes to combine into a single route.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto py-4">
            <div className="space-y-2 mb-4">
              <Label className="text-sm font-medium">Select routes to combine:</Label>
              <div className="border border-slate-200 rounded-md max-h-48 overflow-auto">
                {options.map((route) => (
                  <div
                    key={route}
                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-0 ${combineSelected.includes(route) ? 'bg-blue-50' : ''}`}
                    onClick={() => toggleCombineSelection(route)}
                  >
                    <div className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${combineSelected.includes(route) ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                      {combineSelected.includes(route) && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span className="text-sm">{route}</span>
                  </div>
                ))}
              </div>
              {combineSelected.length > 0 && (
                <p className="text-xs text-slate-500">
                  Selected: {combineSelected.join(', ')}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">New combined route name:</Label>
              <Input
                value={newCombinedName}
                onChange={(e) => setNewCombinedName(e.target.value)}
                placeholder="Enter name for combined route"
                className="h-10"
              />
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowCombineModal(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={executeCombine}
              disabled={combineSelected.length < 2 || !newCombinedName.trim()}
            >
              <Combine className="h-4 w-4 mr-1" />
              Combine Routes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Enhanced Area Multi-Select Dropdown with Edit and Combine features (with combine confirmation)
const AreaDropdown = ({ options, selected, onChange, placeholder, label, onAreaRename, onAreasCombine }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [pendingEditArea, setPendingEditArea] = useState(null);
  const [showCombineConfirm, setShowCombineConfirm] = useState(false);
  const [showCombineModal, setShowCombineModal] = useState(false);
  const [combineSelected, setCombineSelected] = useState([]);
  const [newCombinedName, setNewCombinedName] = useState('');
  const dropdownRef = useRef(null);
  const editInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (!showEditConfirm && !showCombineModal && !showCombineConfirm) {
          setIsOpen(false);
          setEditingArea(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEditConfirm, showCombineModal, showCombineConfirm]);

  useEffect(() => {
    if (editingArea && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingArea]);

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const selectAll = () => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const getDisplayText = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length === options.length) return `All ${label}`;
    if (selected.length === 1) return selected[0];
    return `${selected.length} ${label}`;
  };

  const handleEditClick = (e, area) => {
    e.stopPropagation();
    setPendingEditArea(area);
    setShowEditConfirm(true);
  };

  const confirmEdit = () => {
    setEditingArea(pendingEditArea);
    setEditValue(pendingEditArea);
    setShowEditConfirm(false);
    setPendingEditArea(null);
  };

  const cancelEdit = () => {
    setShowEditConfirm(false);
    setPendingEditArea(null);
  };

  const saveEdit = async (e) => {
    e.stopPropagation();
    if (editValue.trim() && editValue !== editingArea) {
      await onAreaRename(editingArea, editValue.trim());
    }
    setEditingArea(null);
    setEditValue('');
  };

  const cancelEditMode = (e) => {
    e.stopPropagation();
    setEditingArea(null);
    setEditValue('');
  };

  const handleCombineClick = () => {
    setShowCombineConfirm(true);
  };

  const confirmCombineOpen = () => {
    setShowCombineConfirm(false);
    setCombineSelected([]);
    setNewCombinedName('');
    setShowCombineModal(true);
  };

  const toggleCombineSelection = (area) => {
    if (combineSelected.includes(area)) {
      setCombineSelected(combineSelected.filter(a => a !== area));
    } else {
      setCombineSelected([...combineSelected, area]);
    }
  };

  const executeCombine = async () => {
    if (combineSelected.length < 2) {
      toast.error('Please select at least 2 areas to combine');
      return;
    }
    if (!newCombinedName.trim()) {
      toast.error('Please enter a name for the combined area');
      return;
    }
    await onAreasCombine(combineSelected, newCombinedName.trim());
    setShowCombineModal(false);
    setCombineSelected([]);
    setNewCombinedName('');
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between h-8 min-w-[140px] max-w-[200px] px-2 text-sm border border-slate-200 rounded-md bg-white hover:bg-slate-50"
        >
          <span className="truncate text-left">{getDisplayText()}</span>
          <ChevronDown className={`h-4 w-4 ml-1 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute z-50 mt-1 min-w-[280px] max-w-[350px] bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-auto">
            {/* Header with Combine Button */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50 sticky top-0">
              <span className="text-xs font-semibold text-slate-600 uppercase">Areas</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 px-2 text-xs border-blue-200 text-blue-600 hover:bg-blue-50"
                onClick={handleCombineClick}
              >
                <Combine className="h-3 w-3 mr-1" />
                Combine
              </Button>
            </div>
            
            {/* Select All Option */}
            <div
              className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 cursor-pointer border-b border-slate-100"
              onClick={selectAll}
            >
              <div className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${selected.length === options.length ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                {selected.length === options.length && <Check className="h-3 w-3 text-white" />}
              </div>
              <span className="text-sm font-medium">Select All</span>
            </div>
            
            {/* Individual Options */}
            {options.map((option) => (
              <div
                key={option}
                className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 group"
              >
                {editingArea === option ? (
                  // Edit mode
                  <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                    <Input
                      ref={editInputRef}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="h-7 text-sm flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(e);
                        if (e.key === 'Escape') cancelEditMode(e);
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white"
                      onClick={saveEdit}
                    >
                      <Save className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2"
                      onClick={cancelEditMode}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  // Normal mode
                  <>
                    <div 
                      className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 cursor-pointer ${selected.includes(option) ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}
                      onClick={() => toggleOption(option)}
                    >
                      {selected.includes(option) && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span 
                      className="text-sm flex-1 cursor-pointer whitespace-normal break-words leading-tight py-0.5"
                      onClick={() => toggleOption(option)}
                    >
                      {option}
                    </span>
                    <button
                      type="button"
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-blue-100 rounded transition-opacity"
                      onClick={(e) => handleEditClick(e, option)}
                      title="Edit area name"
                    >
                      <Edit2 className="h-3 w-3 text-blue-600" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Confirmation Dialog */}
      <Dialog open={showEditConfirm} onOpenChange={setShowEditConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-blue-600" />
              Edit Area?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to edit this area? This will rename "<strong>{pendingEditArea}</strong>" for all stores assigned to it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={cancelEdit}>
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={confirmEdit}>
              Yes, Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Combine Confirmation Dialog */}
      <Dialog open={showCombineConfirm} onOpenChange={setShowCombineConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Combine className="h-5 w-5 text-blue-600" />
              Combine Areas?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to combine areas? This action will merge multiple areas into one, affecting all stores assigned to those areas.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowCombineConfirm(false)}>
              Cancel
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={confirmCombineOpen}>
              Yes, Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Combine Areas Modal */}
      <Dialog open={showCombineModal} onOpenChange={setShowCombineModal}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Combine className="h-5 w-5 text-blue-600" />
              Combine Areas
            </DialogTitle>
            <DialogDescription>
              Select two or more areas to combine into a single area.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto py-4">
            <div className="space-y-2 mb-4">
              <Label className="text-sm font-medium">Select areas to combine:</Label>
              <div className="border border-slate-200 rounded-md max-h-48 overflow-auto">
                {options.map((area) => (
                  <div
                    key={area}
                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-0 ${combineSelected.includes(area) ? 'bg-blue-50' : ''}`}
                    onClick={() => toggleCombineSelection(area)}
                  >
                    <div className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${combineSelected.includes(area) ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                      {combineSelected.includes(area) && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span className="text-sm">{area}</span>
                  </div>
                ))}
              </div>
              {combineSelected.length > 0 && (
                <p className="text-xs text-slate-500">
                  Selected: {combineSelected.join(', ')}
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">New combined area name:</Label>
              <Input
                value={newCombinedName}
                onChange={(e) => setNewCombinedName(e.target.value)}
                placeholder="Enter name for combined area"
                className="h-10"
              />
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowCombineModal(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={executeCombine}
              disabled={combineSelected.length < 2 || !newCombinedName.trim()}
            >
              <Combine className="h-4 w-4 mr-1" />
              Combine Areas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Searchable Dropdown with "Add New" functionality for Route/Area in forms
const SearchableSelectDropdown = ({ options, value, onChange, placeholder, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const showAddNew = searchTerm.trim() && !options.some(
    opt => opt.toLowerCase() === searchTerm.toLowerCase()
  );

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleAddNew = () => {
    const newValue = searchTerm.trim();
    if (newValue) {
      onChange(newValue);
      setIsOpen(false);
      setSearchTerm('');
      toast.success(`New ${label.toLowerCase()} "${newValue}" will be created`);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className="flex items-center h-9 w-full px-3 border border-slate-200 rounded-md bg-white cursor-pointer hover:border-slate-300"
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
      >
        <span className={`flex-1 text-sm ${value ? 'text-slate-900' : 'text-slate-400'}`}>
          {value || placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                ref={inputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={`Search or type new ${label.toLowerCase()}...`}
                className="h-8 pl-8 text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (showAddNew) {
                      handleAddNew();
                    } else if (filteredOptions.length === 1) {
                      handleSelect(filteredOptions[0]);
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-auto">
            {/* Add New Option */}
            {showAddNew && (
              <div
                className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-slate-100 text-blue-600"
                onClick={handleAddNew}
              >
                <Plus className="h-4 w-4" />
                <span className="text-sm font-medium">Add "{searchTerm.trim()}"</span>
              </div>
            )}

            {/* Existing Options */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 ${value === option ? 'bg-blue-50 text-blue-600 font-medium' : 'text-slate-700'}`}
                  onClick={() => handleSelect(option)}
                >
                  {option}
                </div>
              ))
            ) : (
              !showAddNew && (
                <div className="px-3 py-4 text-sm text-slate-400 text-center">
                  No {label.toLowerCase()}s found
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TradersCRM = () => {
  const [customers, setCustomers] = useState([]);
  const [archivedCustomers, setArchivedCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [selectedRoutes, setSelectedRoutes] = useState([]); // Multi-select for routes
  const [selectedAreas, setSelectedAreas] = useState([]); // Multi-select for areas
  const [manageSearch, setManageSearch] = useState('');
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'archived'
  const { callCustomer, isCalling } = useCallCustomer();
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    contact_person: '',
    address: '',
    route: '',
    area: '',
    type: 'medical',
    notes: ''
  });

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers?firm=traders`, { 
        headers: getAuthHeaders() 
      });
      setCustomers(response.data);
    } catch (error) {
      const message = handleApiError(error, 'Failed to fetch customers');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchArchivedCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers/archived?firm=traders`, { 
        headers: getAuthHeaders() 
      });
      setArchivedCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch archived customers');
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchArchivedCustomers();
  }, [fetchCustomers, fetchArchivedCustomers]);

  const handleRouteRename = async (oldName, newName) => {
    try {
      const response = await axios.put(
        `${API}/routes/rename?firm=traders`,
        { old_name: oldName, new_name: newName },
        { headers: getAuthHeaders() }
      );
      toast.success(`Route renamed: "${oldName}" → "${newName}" (${response.data.customers_updated} stores updated)`);
      // Update selected routes if the renamed route was selected
      if (selectedRoutes.includes(oldName)) {
        setSelectedRoutes(selectedRoutes.map(r => r === oldName ? newName : r));
      }
      fetchCustomers();
      fetchArchivedCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename route');
    }
  };

  const handleRoutesCombine = async (routesToCombine, newRouteName) => {
    try {
      const response = await axios.put(
        `${API}/routes/combine?firm=traders`,
        { routes_to_combine: routesToCombine, new_route_name: newRouteName },
        { headers: getAuthHeaders() }
      );
      toast.success(`Combined ${routesToCombine.length} routes into "${newRouteName}" (${response.data.customers_updated} stores updated)`);
      // Clear selected routes that were combined
      setSelectedRoutes(selectedRoutes.filter(r => !routesToCombine.includes(r)));
      fetchCustomers();
      fetchArchivedCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to combine routes');
    }
  };

  const handleAreaRename = async (oldName, newName) => {
    try {
      const response = await axios.put(
        `${API}/areas/rename?firm=traders`,
        { old_name: oldName, new_name: newName },
        { headers: getAuthHeaders() }
      );
      toast.success(`Area renamed: "${oldName}" → "${newName}" (${response.data.customers_updated} stores updated)`);
      // Update selected areas if the renamed area was selected
      if (selectedAreas.includes(oldName)) {
        setSelectedAreas(selectedAreas.map(a => a === oldName ? newName : a));
      }
      fetchCustomers();
      fetchArchivedCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename area');
    }
  };

  const handleAreasCombine = async (areasToCombine, newAreaName) => {
    try {
      const response = await axios.put(
        `${API}/areas/combine?firm=traders`,
        { areas_to_combine: areasToCombine, new_area_name: newAreaName },
        { headers: getAuthHeaders() }
      );
      toast.success(`Combined ${areasToCombine.length} areas into "${newAreaName}" (${response.data.customers_updated} stores updated)`);
      // Clear selected areas that were combined
      setSelectedAreas(selectedAreas.filter(a => !areasToCombine.includes(a)));
      fetchCustomers();
      fetchArchivedCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to combine areas');
    }
  };

  const handleArchive = async (customerId, customerName) => {
    try {
      await axios.post(`${API}/customers/${customerId}/archive`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(`${customerName} marked as ordered! Will reappear in 24 hours.`);
      fetchCustomers();
      fetchArchivedCustomers();
    } catch (error) {
      toast.error('Failed to archive customer');
    }
  };

  const handleUnarchive = async (customerId, customerName) => {
    try {
      await axios.post(`${API}/customers/${customerId}/unarchive`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(`${customerName} restored to active list`);
      fetchCustomers();
      fetchArchivedCustomers();
    } catch (error) {
      toast.error('Failed to unarchive customer');
    }
  };

  // Calculate time remaining for archived customer
  const getTimeRemaining = (archivedAt) => {
    if (!archivedAt) return null;
    const archiveTime = new Date(archivedAt);
    const now = new Date();
    const restoreTime = new Date(archiveTime.getTime() + 24 * 60 * 60 * 1000);
    const diff = restoreTime - now;
    
    if (diff <= 0) return 'Restoring soon...';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, {
          firm: 'traders',
          ...newCustomer
        }, { headers: getAuthHeaders() });
        toast.success('Store updated');
      } else {
        await axios.post(`${API}/customers`, {
          firm: 'traders',
          ...newCustomer
        }, { headers: getAuthHeaders() });
        toast.success('Store added');
      }
      
      setShowAdd(false);
      setEditingCustomer(null);
      setNewCustomer({ name: '', phone: '', contact_person: '', address: '', route: '', area: '', type: 'medical', notes: '' });
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to save store');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setNewCustomer({
      name: customer.name,
      phone: customer.phone,
      contact_person: customer.contact_person || '',
      address: customer.address || '',
      route: customer.route || '',
      area: customer.area || '',
      type: customer.type || 'medical',
      notes: customer.notes || ''
    });
    setShowManage(false);
    setShowAdd(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await axios.delete(`${API}/customers/${id}`, { headers: getAuthHeaders() });
      toast.success('Store deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete store');
    }
  };

  const sendWhatsApp = (customer) => {
    const phone = customer.phone.replace(/[^0-9]/g, '');
    const message = `Hello ${customer.name},\n\nThank you for your business!\n\nShri Kripa Traders`;
    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Get unique routes and areas
  const routes = [...new Set(customers.map(c => c.route).filter(Boolean))].sort();
  const areas = [...new Set(customers.map(c => c.area).filter(Boolean))].sort();

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.phone.includes(searchQuery) ||
      (customer.contact_person && customer.contact_person.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesType = filterType === 'all' || customer.type === filterType;
    const matchesRoute = selectedRoutes.length === 0 || selectedRoutes.includes(customer.route);
    const matchesArea = selectedAreas.length === 0 || selectedAreas.includes(customer.area);
    
    return matchesSearch && matchesType && matchesRoute && matchesArea;
  });

  // Filter for manage dialog
  const managedCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(manageSearch.toLowerCase()) ||
    customer.phone.includes(manageSearch)
  );

  // Dynamic counts based on filtered results (for routes/areas filter)
  const baseFilteredCustomers = customers.filter(customer => {
    const matchesRoute = selectedRoutes.length === 0 || selectedRoutes.includes(customer.route);
    const matchesArea = selectedAreas.length === 0 || selectedAreas.includes(customer.area);
    return matchesRoute && matchesArea;
  });

  const filteredTotal = baseFilteredCustomers.length;
  const medicalCount = baseFilteredCustomers.filter(c => c.type === 'medical').length;
  const generalCount = baseFilteredCustomers.filter(c => c.type === 'general').length;
  const hospitalCount = baseFilteredCustomers.filter(c => c.type === 'hospital').length;

  // Check if filters are applied
  const hasFilters = selectedRoutes.length > 0 || selectedAreas.length > 0;

  const getTypeColor = (type) => {
    switch(type) {
      case 'medical': return 'bg-blue-100 text-blue-700';
      case 'general': return 'bg-emerald-100 text-emerald-700';
      case 'hospital': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  // Print filtered stores
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    const currentDate = new Date().toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

    const filterInfo = [];
    if (filterType !== 'all') filterInfo.push(`Type: ${filterType}`);
    if (selectedRoutes.length > 0) filterInfo.push(`Routes: ${selectedRoutes.join(', ')}`);
    if (selectedAreas.length > 0) filterInfo.push(`Areas: ${selectedAreas.join(', ')}`);
    if (searchQuery) filterInfo.push(`Search: "${searchQuery}"`);
    
    const filterText = filterInfo.length > 0 ? filterInfo.join(' | ') : 'All Stores';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Store List - Shri Kripa Traders</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 11px;
            line-height: 1.4;
            padding: 15px;
            color: #000;
          }
          .header {
            text-align: center;
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 2px solid #000;
          }
          .header h1 {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 3px;
          }
          .header .subtitle {
            font-size: 12px;
            color: #444;
          }
          .meta {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            font-size: 10px;
            color: #666;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #333;
            padding: 6px 8px;
            text-align: left;
          }
          th {
            background-color: #f0f0f0;
            font-weight: bold;
            font-size: 10px;
            text-transform: uppercase;
          }
          td {
            font-size: 11px;
          }
          .sno { width: 30px; text-align: center; }
          .name { width: 30%; }
          .type { width: 12%; text-align: center; }
          .route { width: 12%; }
          .area { width: 18%; }
          .phone { width: 18%; }
          .type-badge {
            display: inline-block;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 9px;
            font-weight: bold;
            text-transform: uppercase;
          }
          .type-medical { background: #dbeafe; }
          .type-general { background: #d1fae5; }
          .type-hospital { background: #ede9fe; }
          .footer {
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px solid #ccc;
            font-size: 9px;
            color: #666;
            text-align: center;
          }
          @media print {
            body { padding: 10px; }
            .header { margin-bottom: 10px; }
          }
          @page {
            size: A4;
            margin: 10mm;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>SHRI KRIPA TRADERS</h1>
          <div class="subtitle">Store List</div>
        </div>
        
        <div class="meta">
          <span>Filter: ${filterText}</span>
          <span>Date: ${currentDate} | Total: ${filteredCustomers.length} stores</span>
        </div>
        
        <table>
          <thead>
            <tr>
              <th class="sno">#</th>
              <th class="name">Store Name</th>
              <th class="type">Type</th>
              <th class="route">Route</th>
              <th class="area">Area</th>
              <th class="person">Contact Person</th>
              <th class="phone">Phone</th>
            </tr>
          </thead>
          <tbody>
            ${filteredCustomers.map((customer, index) => `
              <tr>
                <td class="sno">${index + 1}</td>
                <td class="name">${customer.name}</td>
                <td class="type">
                  <span class="type-badge type-${customer.type}">${customer.type}</span>
                </td>
                <td class="route">${customer.route || '-'}</td>
                <td class="area">${customer.area || '-'}</td>
                <td class="person">${customer.contact_person || '-'}</td>
                <td class="phone">${customer.phone}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          Printed from Shri Kripa Traders CRM
        </div>
        
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `;

    // Use Blob URL approach for safer HTML rendering (prevents XSS)
    const blob = new Blob([printContent], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    printWindow.location.href = blobUrl;
    
    // Clean up blob URL after print window loads
    printWindow.onload = () => {
      URL.revokeObjectURL(blobUrl);
    };
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-96"><div className="text-slate-500">Loading...</div></div></Layout>;

  return (
    <Layout>
      <div className="space-y-3">
        {/* Compact Header with Stats & Filters */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg p-3">
          {/* Left: Title + Dynamic Stats inline */}
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-slate-900">CRM</h1>
            <div className="hidden sm:flex items-center gap-3 text-sm">
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded ${hasFilters ? 'bg-blue-100 ring-1 ring-blue-300' : 'bg-slate-100'}`}>
                <Store className="h-3.5 w-3.5 text-slate-600" />
                <span className="font-medium">{filteredTotal}</span>
                <span className="text-slate-500">{hasFilters ? 'Filtered' : 'Total'}</span>
              </div>
              <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded">
                <ShoppingBag className="h-3.5 w-3.5 text-blue-600" />
                <span className="font-medium text-blue-600">{medicalCount}</span>
                <span className="text-blue-500">Medical</span>
              </div>
              <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-50 rounded">
                <Store className="h-3.5 w-3.5 text-emerald-600" />
                <span className="font-medium text-emerald-600">{generalCount}</span>
                <span className="text-emerald-500">General</span>
              </div>
              <div className="flex items-center gap-1.5 px-2 py-1 bg-purple-50 rounded">
                <Building2 className="h-3.5 w-3.5 text-purple-600" />
                <span className="font-medium text-purple-600">{hospitalCount}</span>
                <span className="text-purple-500">Hospital</span>
              </div>
              {hasFilters && (
                <button 
                  onClick={() => { setSelectedRoutes([]); setSelectedAreas([]); }}
                  className="text-xs text-red-600 hover:text-red-700 underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Right: Search + Multi-Select Filters + Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 w-40 text-sm border-slate-200"
              />
            </div>
            
            {/* Multi-Select Route Filter */}
            <RouteDropdown
              options={routes}
              selected={selectedRoutes}
              onChange={setSelectedRoutes}
              placeholder="All Routes"
              label="Routes"
              onRouteRename={handleRouteRename}
              onRoutesCombine={handleRoutesCombine}
            />
            
            {/* Multi-Select Area Filter */}
            <AreaDropdown
              options={areas}
              selected={selectedAreas}
              onChange={setSelectedAreas}
              placeholder="All Areas"
              label="Areas"
              onAreaRename={handleAreaRename}
              onAreasCombine={handleAreasCombine}
            />
            
            {/* Print Button */}
            <Button 
              size="sm" 
              variant="outline" 
              className="h-8 text-slate-600"
              onClick={handlePrint}
              title="Print filtered store list"
            >
              <Printer className="h-4 w-4 mr-1" />
              Print
            </Button>
            
            {/* Manage Stores Button */}
            <Button 
              size="sm" 
              variant="outline" 
              className="h-8 text-slate-600"
              onClick={() => setShowManage(true)}
            >
              <Settings className="h-4 w-4 mr-1" />
              Manage
            </Button>

            {/* Add Store Dialog */}
            <Dialog open={showAdd} onOpenChange={(open) => {
              setShowAdd(open);
              if (!open) {
                setEditingCustomer(null);
                setNewCustomer({ name: '', phone: '', contact_person: '', address: '', route: '', area: '', type: 'medical', notes: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="h-4 w-4 mr-1" />Add
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl font-semibold">
                    {editingCustomer ? 'Edit Store' : 'Add New Store'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-sm">Store Name *</Label>
                    <Input value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} required className="h-9" placeholder="ABC Medical Store" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Store Type *</Label>
                    <Select value={newCustomer.type} onValueChange={(val) => setNewCustomer({ ...newCustomer, type: val })}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="medical">Medical Store</SelectItem>
                        <SelectItem value="general">General Store</SelectItem>
                        <SelectItem value="hospital">Hospital</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-sm">Route *</Label>
                      <SearchableSelectDropdown
                        options={routes}
                        value={newCustomer.route}
                        onChange={(val) => setNewCustomer({ ...newCustomer, route: val })}
                        placeholder="Select or add route"
                        label="Route"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Area *</Label>
                      <SearchableSelectDropdown
                        options={areas}
                        value={newCustomer.area}
                        onChange={(val) => setNewCustomer({ ...newCustomer, area: val })}
                        placeholder="Select or add area"
                        label="Area"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-sm">Phone *</Label>
                      <Input value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} required className="h-9" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Contact Person
                      </Label>
                      <Input value={newCustomer.contact_person} onChange={(e) => setNewCustomer({ ...newCustomer, contact_person: e.target.value })} className="h-9" placeholder="Owner/Manager name" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Address</Label>
                    <Textarea value={newCustomer.address} onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })} rows={2} className="border-slate-200 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Notes</Label>
                    <Textarea value={newCustomer.notes} onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })} rows={2} className="border-slate-200 text-sm" placeholder="Any additional information..." />
                  </div>
                  <Button type="submit" className="w-full h-9 bg-blue-600 hover:bg-blue-700 text-white">
                    {editingCustomer ? 'Update Store' : 'Add Store'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Mobile Stats (shown on small screens) */}
        <div className="sm:hidden flex items-center gap-2 text-xs overflow-x-auto pb-1">
          <div className={`flex items-center gap-1 px-2 py-1 rounded whitespace-nowrap ${hasFilters ? 'bg-blue-100 ring-1 ring-blue-300' : 'bg-slate-100'}`}>
            <span className="font-medium">{filteredTotal}</span>
            <span className="text-slate-500">{hasFilters ? 'Filtered' : 'Total'}</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded whitespace-nowrap">
            <span className="font-medium text-blue-600">{medicalCount}</span>
            <span className="text-blue-500">Medical</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 rounded whitespace-nowrap">
            <span className="font-medium text-emerald-600">{generalCount}</span>
            <span className="text-emerald-500">General</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 rounded whitespace-nowrap">
            <span className="font-medium text-purple-600">{hospitalCount}</span>
            <span className="text-purple-500">Hospital</span>
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 rounded whitespace-nowrap">
            <span className="font-medium text-amber-600">{archivedCustomers.length}</span>
            <span className="text-amber-500">Archived</span>
          </div>
          {hasFilters && (
            <button 
              onClick={() => { setSelectedRoutes([]); setSelectedAreas([]); }}
              className="text-xs text-red-600 whitespace-nowrap underline"
            >
              Clear
            </button>
          )}
        </div>

        {/* View Mode Toggle: Active vs Archived */}
        <div className="flex items-center gap-2 mb-3">
          <Button
            size="sm"
            variant={viewMode === 'active' ? 'default' : 'outline'}
            className={`h-8 ${viewMode === 'active' ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
            onClick={() => setViewMode('active')}
          >
            <Store className="h-4 w-4 mr-1" />
            Active ({customers.length})
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'archived' ? 'default' : 'outline'}
            className={`h-8 ${viewMode === 'archived' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
            onClick={() => setViewMode('archived')}
          >
            <Archive className="h-4 w-4 mr-1" />
            Archived ({archivedCustomers.length})
          </Button>
        </div>

        {/* Active View */}
        {viewMode === 'active' && (
          <Tabs value={filterType} onValueChange={setFilterType}>
            <TabsList className="h-8 p-0.5">
              <TabsTrigger value="all" className="h-7 text-xs px-3">All</TabsTrigger>
              <TabsTrigger value="medical" className="h-7 text-xs px-3">Medical</TabsTrigger>
              <TabsTrigger value="general" className="h-7 text-xs px-3">General</TabsTrigger>
              <TabsTrigger value="hospital" className="h-7 text-xs px-3">Hospital</TabsTrigger>
            </TabsList>

            <TabsContent value={filterType} className="mt-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                {filteredCustomers.length === 0 ? (
                  <Card className="col-span-full p-8 text-center border-slate-200">
                    <p className="text-slate-600 mb-3 text-sm">No stores found</p>
                    <Button onClick={() => setShowAdd(true)} className="bg-blue-600 hover:bg-blue-700 text-white" size="sm">
                      <Plus className="h-3 w-3 mr-1" />Add Store
                    </Button>
                  </Card>
                ) : (
                  filteredCustomers.map((customer) => (
                    <Card key={customer.id} className="border-slate-200 hover:shadow-md transition-shadow">
                      <CardContent className="p-3">
                        <h3 className="text-xs font-semibold text-slate-900 leading-tight mb-1 line-clamp-2 min-h-[2.5rem]" title={customer.name}>
                          {customer.name}
                        </h3>
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${getTypeColor(customer.type)}`}>
                          {customer.type}
                        </span>
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Route className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">Route: {customer.route}</span>
                          </div>
                          {customer.contact_person && (
                            <div className="flex items-center gap-1 text-xs text-slate-600">
                              <User className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{customer.contact_person}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1 text-xs text-slate-600 font-medium">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{customer.phone}</span>
                          </div>
                        </div>
                        <div className="mt-2 flex gap-1">
                          <Button 
                            onClick={() => sendWhatsApp(customer)}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white h-7 text-xs px-1"
                            size="sm"
                            title="WhatsApp"
                          >
                            <MessageCircle className="h-3 w-3" />
                          </Button>
                          <Button 
                            onClick={() => callCustomer(customer.phone, customer.name)}
                            disabled={isCalling}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs px-1"
                            size="sm"
                            title="Call"
                          >
                            <Phone className="h-3 w-3" />
                          </Button>
                          <Button 
                            onClick={() => handleArchive(customer.id, customer.name)}
                            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white h-7 text-xs px-1"
                            size="sm"
                            title="Mark as Ordered"
                          >
                            <CheckCircle className="h-3 w-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Archived View */}
        {viewMode === 'archived' && (
          <div className="space-y-3">
            <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <Archive className="h-4 w-4 inline mr-1" />
                Archived stores will automatically return to active list after 24 hours.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2">
              {archivedCustomers.length === 0 ? (
                <Card className="col-span-full p-8 text-center border-slate-200">
                  <Archive className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-600 text-sm">No archived stores</p>
                  <p className="text-slate-400 text-xs mt-1">Stores marked as "Ordered" will appear here</p>
                </Card>
              ) : (
                archivedCustomers.map((customer) => (
                  <Card key={customer.id} className="border-amber-200 bg-amber-50/50 hover:shadow-md transition-shadow">
                    <CardContent className="p-3">
                      <h3 className="text-xs font-semibold text-slate-900 leading-tight mb-1 line-clamp-2 min-h-[2.5rem]" title={customer.name}>
                        {customer.name}
                      </h3>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${getTypeColor(customer.type)}`}>
                        {customer.type}
                      </span>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Route className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">Route: {customer.route}</span>
                        </div>
                        {customer.contact_person && (
                          <div className="flex items-center gap-1 text-xs text-slate-600">
                            <User className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{customer.contact_person}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                          <Archive className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate text-[10px]">{getTimeRemaining(customer.archived_at)}</span>
                        </div>
                      </div>
                      <Button 
                        onClick={() => handleUnarchive(customer.id, customer.name)}
                        className="w-full mt-2 bg-slate-600 hover:bg-slate-700 text-white h-7 text-xs"
                        size="sm"
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        Restore Now
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Manage Stores Dialog */}
        <Dialog open={showManage} onOpenChange={setShowManage}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader className="pb-2">
              <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                <Settings className="h-5 w-5 text-slate-600" />
                Manage Stores
              </DialogTitle>
            </DialogHeader>
            
            {/* Search in Manage Dialog */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search stores to edit or delete..."
                value={manageSearch}
                onChange={(e) => setManageSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>

            {/* Stores List */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {managedCustomers.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  No stores found
                </div>
              ) : (
                managedCustomers.map((customer) => (
                  <div 
                    key={customer.id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-900 truncate">{customer.name}</h4>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium capitalize ${getTypeColor(customer.type)}`}>
                          {customer.type}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Route className="h-3 w-3" />
                          Route: {customer.route}
                        </span>
                        {customer.contact_person && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {customer.contact_person}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {customer.phone}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-8 px-3 text-blue-600 border-blue-200 hover:bg-blue-50"
                        onClick={() => handleEdit(customer)}
                      >
                        <Edit className="h-3.5 w-3.5 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-8 px-3 text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleDelete(customer.id, customer.name)}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="pt-3 border-t border-slate-200 flex justify-between items-center">
              <p className="text-xs text-slate-500">{customers.length} stores total</p>
              <Button variant="outline" onClick={() => setShowManage(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TradersCRM;
